import React from "react";

// Pull the normalised pieces out of a GET /courses/:id response.
export const parseCourseDetail = (data = {}) => {
  const course = data?.course || data || {};
  const modules = (data?.modules || course?.modules || [])
    .slice()
    .sort((a, b) => (a?.order || 0) - (b?.order || 0));
  const courseQuiz = data?.courseQuiz || course?.quizDoc || null;
  const contentIndex = data?.contentIndex || course?.contentIndex || null;
  return { course, modules, courseQuiz, contentIndex };
};

/* ───────────── Asset helpers ───────────── */

export const assetIcon = (type) =>
  type === "video"
    ? "fas fa-video"
    : type === "pdf"
    ? "fas fa-file-pdf"
    : type === "image"
    ? "fas fa-image"
    : "fas fa-file";

export const formatDuration = (s) => {
  const sec = Number(s);
  if (!sec) return null;
  const m = Math.floor(sec / 60);
  const ss = sec % 60;
  return m ? `${m}m${ss ? " " + ss + "s" : ""}` : `${ss}s`;
};

const comparableAssetName = (value = "") => {
  const withoutQuery = String(value || "").split("?")[0];
  const lastSegment = withoutQuery.split("/").filter(Boolean).pop() || withoutQuery;
  let decoded = lastSegment;
  try {
    decoded = decodeURIComponent(lastSegment);
  } catch (error) {
    // Keep the original text when a legacy filename contains malformed escapes.
  }
  return decoded
    .replace(/\.[a-z0-9]{1,8}$/i, "")
    .trim()
    .replace(/\s+/g, " ")
    .toLocaleLowerCase();
};

// Show the physical filename only when it adds information beyond the title.
export const distinctAssetFileName = (displayTitle, fileName) => {
  const cleanFileName = String(fileName || "").trim();
  if (!cleanFileName) return "";
  return comparableAssetName(displayTitle) === comparableAssetName(cleanFileName)
    ? ""
    : cleanFileName;
};

// Strip an uploaded/loaded asset down to the fields the backend stores, so we
// never send transient bits like `previewUrl`, `_id`, or `sizeMb` on attach.
export const assetPayload = (a = {}) => ({
  type: a.type,
  title: a.title || "",
  url: a.url,
  ...(a.description ? { description: a.description } : {}),
  ...(a.durationSeconds != null ? { durationSeconds: Number(a.durationSeconds) } : {}),
  ...(a.hasVoiceOver ? { hasVoiceOver: true } : {}),
  ...(a.order != null ? { order: a.order } : {}),
});

export const assetIdOf = (a = {}) => a._id || a.assetId;

export const mergeWrappedAssetsByKey = (
  existingItems = [],
  incomingItems = [],
  wrapKey
) => {
  const merged = new Map();

  existingItems.forEach((item) => {
    if (item?.[wrapKey]) merged.set(item[wrapKey], item);
  });

  incomingItems.forEach((item) => {
    if (item?.[wrapKey]) merged.set(item[wrapKey], item);
  });

  return Array.from(merged.values());
};

/* ───────────── Learner-preview nodes + completion ───────────── */

const bySortOrder = (a, b) => (a?.order || 0) - (b?.order || 0);
const idOfModule = (m) => m?._id || m?.moduleId;
const idOfSection = (s) => s?._id || s?.sectionId;
const previewQuizOf = (item) => item?.quizDoc || item?.quiz || null;
const previewQuizIdOf = (quiz) =>
  quiz?._id || quiz?.quizId || (typeof quiz === "string" ? quiz : null);

// Flatten a server preview payload ({ introAsset, modules, finalQuiz }) into the
// ordered list of preview "nodes" a learner steps through. Persona filtering is
// already done server-side (introAsset is the single matching asset;
// supportServiceAssets is the filtered list). Section/final quizzes arrive as
// summaries — nodes carry `quizId` and the quiz is loaded on demand.
export const buildPreviewNodes = (preview = {}) => {
  const { introAsset, modules = [], finalQuiz } = preview;
  const nodes = [];

  if (introAsset) {
    nodes.push({ key: "course-intro", kind: "courseIntro", asset: introAsset });
  }

  [...modules].sort(bySortOrder).forEach((m, moduleIndex) => {
    const mid = idOfModule(m);
    const moduleTitle = m?.title || "";
    const introAssets = (m?.introductionAssets || []).slice().sort(bySortOrder);
    if (introAssets.length) {
      nodes.push({
        key: `mi-${mid}`,
        kind: "moduleIntro",
        moduleId: mid,
        moduleIndex: moduleIndex + 1,
        moduleTitle,
        assets: introAssets,
      });
    }
    [...(m?.sections || [])].sort(bySortOrder).forEach((s, i) => {
      const q = previewQuizOf(s);
      nodes.push({
        key: `sec-${idOfSection(s)}`,
        kind: "section",
        moduleId: mid,
        moduleIndex: moduleIndex + 1,
        moduleTitle,
        sectionId: idOfSection(s),
        sectionTitle: s?.title || "",
        partIndex: i + 1,
        assets: (s?.assets || []).slice().sort(bySortOrder),
        quiz: q,
        quizId: previewQuizIdOf(q),
        quizStatus: q?.status || null,
      });
    });
    const completion = (m?.completionAssets || []).slice().sort(bySortOrder);
    if (completion.length) {
      nodes.push({
        key: `mc-${mid}`,
        kind: "completion",
        moduleId: mid,
        moduleIndex: moduleIndex + 1,
        moduleTitle,
        assets: completion,
      });
    }
    // supportServiceAssets is already filtered to the persona's area (a list).
    const support = (m?.supportServiceAssets || m?.supportServicesAssets || [])
      .slice()
      .sort(bySortOrder);
    if (support.length) {
      nodes.push({
        key: `ms-${mid}`,
        kind: "support",
        moduleId: mid,
        moduleIndex: moduleIndex + 1,
        moduleTitle,
        assets: support,
      });
    }
  });

  if (finalQuiz) {
    nodes.push({
      key: "final-quiz",
      kind: "finalQuiz",
      quiz: finalQuiz,
      quizId: finalQuiz.quizId || finalQuiz._id || null,
      quizStatus: finalQuiz.status || null,
    });
  }
  return nodes;
};

// In-memory progress over the preview nodes (no persistence).
export const previewProgress = (nodes = [], done) => {
  const set = done || new Set();
  const total = nodes.length;
  const doneCount = nodes.filter((n) => set.has(n.key)).length;
  const byModule = {};
  nodes.forEach((n) => {
    if (!n.moduleId) return;
    byModule[n.moduleId] = byModule[n.moduleId] || { done: 0, total: 0 };
    byModule[n.moduleId].total += 1;
    if (set.has(n.key)) byModule[n.moduleId].done += 1;
  });
  return {
    total,
    doneCount,
    byModule,
    courseComplete: total > 0 && doneCount === total,
    percent: total ? Math.round((doneCount / total) * 100) : 0,
  };
};

const isPublished = (q) => (q?.status || "").toLowerCase() === "published";
const sectionsOf = (m) => m?.sections || [];
const moduleHasContent = (m) =>
  sectionsOf(m).some((s) => (s?.assets || []).length > 0);
const sectionQuiz = (s) => s?.quizDoc || (s?.quiz ? { status: "draft" } : null);

// Compute the course readiness checklist + an overall percentage over the
// asset-based tree. Used by the wizard Review step and workspace Overview tab.
export const computeReadiness = (course, modules = [], _courseQuiz, t) => {
  const checks = [];
  const hasModules = modules.length > 0;

  checks.push({
    key: "info",
    label: t("admin.lms.wizard.checkInfo"),
    ok: !!course?.title?.trim(),
  });

  // Course intro videos (when required).
  if (course?.completionCriteria?.requireIntroVideo) {
    const introCount = (course?.introAssets || []).length;
    checks.push({
      key: "intro",
      label: t("admin.lms.wizard.checkIntro"),
      ok: introCount > 0,
      warn: introCount === 0,
    });
  }

  checks.push({
    key: "modules",
    label: t("admin.lms.wizard.checkModules"),
    ok: hasModules,
  });

  checks.push({
    key: "publishedModule",
    label: t("admin.lms.wizard.checkPublishedModule"),
    ok: modules.some((m) => m?.isPublished),
  });

  // Every module should have at least one section carrying an asset.
  const allHaveContent = hasModules && modules.every(moduleHasContent);
  checks.push({
    key: "content",
    label: t("admin.lms.wizard.checkContent"),
    ok: allHaveContent,
    warn: !allHaveContent && hasModules,
  });

  // Section quizzes (when the course requires them).
  if (course?.hasQuizPerSection || course?.completionCriteria?.requireSectionQuizPass) {
    const sections = modules.flatMap(sectionsOf);
    const allPublished =
      sections.length > 0 &&
      sections.every((s) => {
        const q = sectionQuiz(s);
        return q && isPublished(q);
      });
    checks.push({
      key: "sectionQuizzes",
      label: t("admin.lms.wizard.checkSectionQuizzes"),
      ok: allPublished,
      warn: !allPublished,
    });
  }

  const passed = checks.filter((c) => c.ok).length;
  const score = checks.length
    ? Math.round((passed / checks.length) * 100)
    : 0;
  return { checks, score, passed, total: checks.length };
};

// Small circular readiness gauge.
export const ReadinessRing = ({ score = 0, size = 132 }) => {
  const r = (size - 10) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (score / 100) * c;
  const tone = score >= 100 ? "#2e9e57" : score >= 60 ? "#eaa61c" : "#d33";
  return (
    <svg width={size} height={size} className="lms-readiness-ring">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="#eef0f6"
        strokeWidth="8"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={tone}
        strokeWidth="8"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dashoffset 0.5s ease" }}
      />
      <text
        x="50%"
        y="50%"
        dominantBaseline="central"
        textAnchor="middle"
        fontSize="20"
        fontWeight="800"
        fill="#1f2a44"
      >
        {score}%
      </text>
    </svg>
  );
};

// Reusable labelled checkbox toggle.
export const LmsToggle = ({ id, checked, onChange, label, hint }) => (
  <div className="lms-toggle-field">
    <div className="d-flex align-items-center">
      <input
        id={id}
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mr-2"
      />
      <label htmlFor={id} className="mb-0">
        {label}
      </label>
    </div>
    {hint && <div className="small text-muted lms-toggle-hint">{hint}</div>}
  </div>
);
